import React, { useState, useEffect } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface EditCommentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialNotes: string;
    onSave: (newNotes: string) => Promise<void>;
    isSaving?: boolean;
}

export const EditCommentsModal: React.FC<EditCommentsModalProps> = ({
    isOpen,
    onClose,
    initialNotes,
    onSave,
    isSaving = false,
}) => {
    const [notes, setNotes] = useState(initialNotes);
    const [error, setError] = useState<string | null>(null);

    // Update local state when initialNotes changes
    useEffect(() => {
        setNotes(initialNotes === 'Empty' ? '' : initialNotes);
    }, [initialNotes]);

    if (!isOpen) return null;

    const handleSave = async () => {
        try {
            setError(null);
            await onSave(notes);
            // onClose will be called by parent after successful save
        } catch (err: any) {
            setError(err.message || 'Failed to save comments');
        }
    };

    const handleCancel = () => {
        setNotes(initialNotes === 'Empty' ? '' : initialNotes);
        setError(null);
        onClose();
    };

    return (
        <ModalWrapper modalId="EditCommentsModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Edit Notes"
            isLoading={isSaving}
        >
            {/* Text Area */}
            <div className="mb-6">
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className={`${POPUP_STYLES.input} resize-none`}
                    rows={6}
                    placeholder="Enter notes..."
                    disabled={isSaving}
                />
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Buttons */}
            <div className="flex justify-center gap-4">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className={POPUP_STYLES.button}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
